<script setup lang="ts">
import GroovizAmount from '~/components/ui/GroovizAmount.vue'
import VText from '~/components/ui/VText.vue'

import { useNavigateToPreviousPage } from '~/composables/useNavigateToPreviousPage'

import { useCartPromoStore } from '~/stores/cartPromo'
import { useDraftStore } from '~/stores/draft'
import { useMiscDraftProgressivePromosStore } from '~/stores/miscDraftProgressivePromos'
import { useMiscResizeStore } from '~/stores/miscResize'

import { Breakpoints } from '~/enums/breakpoints'

import type { PropConfigType } from '~/components/ui/VText.vue'

type Props = {
  influencersCount: number
  campaignCost: number
  displayedCost: number
  hideGrooviz?: boolean
  selectedWording?: boolean
}
const {
  influencersCount,
  displayedCost,
  campaignCost,
  hideGrooviz = false,
  selectedWording = false,
} = defineProps<Props>()
const { navigateToPreviousPage } = useNavigateToPreviousPage()
const { t } = useI18n()
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { PROGRESSIVE_PROMO_FROM_INF_COUNT } =
  useMiscDraftProgressivePromosStore()
const { $state: DRAFT } = useDraftStore()
const { $state: CART_PROMO } = useCartPromoStore()
const text = computed(() => {
  if (!selectedWording) {
    return t('components.campaignFooter.cartCounter.preselectedPros', {
      count: influencersCount,
    })
  }
  const count = influencersCount
  return SCREEN_WIDTH.value >= Breakpoints.sm600
    ? t(`components.campaignFooter.cartCounter.selectedProsDesktop`, { count })
    : t(`components.campaignFooter.cartCounter.selectedProsMobile`, { count })
})
const eligibleProgressivePromo = computed(() => {
  return PROGRESSIVE_PROMO_FROM_INF_COUNT(DRAFT.influencers_count)
})
const promoText = computed(() => {
  const promoToDisplay =
    !CART_PROMO.id && eligibleProgressivePromo?.value
      ? eligibleProgressivePromo?.value
      : CART_PROMO
  const promoValue = promoToDisplay.actions.campaign.value ?? 0
  const isPercentage = promoToDisplay.actions.campaign.is_percentage ?? false
  return `-${promoValue}${isPercentage ? '%' : ''}`
})
const textConfig = computed<PropConfigType>(() =>
  SCREEN_WIDTH.value >= Breakpoints.sm600 ? 'sans/16/medium' : 'sans/14/medium',
)
const route = useRoute()
const isCartDisplayed = computed(
  () => route.name?.toString().startsWith('draft-influencers__') ?? false,
)
const { ranks: RANKED_INFLUENCERS } = storeToRefs(
  useInfluencersRecommendationsStore(),
)
function handleHideCart(): void {
  const { draftId } = route.query
  const hash = RANKED_INFLUENCERS.value.length > 0 ? '#loaded' : ''
  navigateToPreviousPage(
    draftId ? `/draft/${draftId}/influencers/${hash}` : '/',
  )
}
</script>
<template>
  <div class="tw-flex">
    <NuxtLinkLocale
      v-if="DRAFT.id && !isCartDisplayed && !!DRAFT.influencers_count"
      :to="`/draft/influencers/?draftId=${DRAFT.id}`"
    >
      <VText
        :cfg="textConfig"
        color="orange-500"
        class="hover:tw-underline"
        data-test-id="influencerSelectedCounter"
      >
        {{ text }}
      </VText>
    </NuxtLinkLocale>
    <VText
      v-else-if="isCartDisplayed"
      :cfg="textConfig"
      color="orange-500"
      class="tw-pointer tw-cursor-pointer lg:hover:tw-underline"
      @click="handleHideCart"
    >
      {{ t('components.campaignFooter.toggleCart.hide') }}
    </VText>
    <VText v-else cfg="sans/16/medium" color="black">
      {{ text }}
    </VText>
    <div v-if="!hideGrooviz" class="tw-mx-1 tw-flex tw-gap-1">
      <span class="tw-font-bold">•</span>
      <VText :cfg="textConfig" color="black">
        <GroovizAmount :amount="displayedCost" />
      </VText>
      <transition name="fade">
        <div
          v-if="DRAFT.id && (CART_PROMO.id || eligibleProgressivePromo?.id)"
          class="tw-flex tw-items-center tw-justify-start tw-gap-1"
        >
          <VText :cfg="textConfig" color="gray-500">
            <GroovizAmount :amount="campaignCost" strikethrough />
          </VText>
          <span>
            <VText :cfg="textConfig" color="blue-500">
              {{ promoText }}
            </VText>
            <i
              v-if="!CART_PROMO.actions.campaign.is_percentage"
              class="icon-grooviz tw-text-base tw-font-medium tw-text-orange-500"
            />
          </span>
        </div>
      </transition>
    </div>
  </div>
</template>
